import { createFileRoute } from "@tanstack/react-router"
import {
  Container,
  Title,
  Timeline,
  Text,
  Badge,
  Box,
  Stack,
  Group,
  Paper,
  Tabs,
  TextInput,
  Select,
  useMantineColorScheme,
} from "@mantine/core"
import { IconCalendar, IconGavel, IconNews, IconFileText, IconClock, IconSearch } from "@tabler/icons-react"
import { timelineEvents } from "@/data/timeline-data"
import { useState, useMemo } from "react"
import { SEO } from "@/components/seo/SEO"
import { StructuredData, createEventSchema, createItemListSchema } from "@/components/seo/StructuredData"
import { PageCTASection } from "@/components/ui/PageCTASection"

export const Route = createFileRoute("/timeline")({
  component: TimelinePage,
})

function TimelinePage() {
  const [activeTab, setActiveTab] = useState<string>("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [significanceFilter, setSignificanceFilter] = useState<string>("all")
  const { colorScheme } = useMantineColorScheme()

  const filteredEvents = useMemo(() => {
    const decade = activeTab !== "all" ? parseInt(activeTab) : null
    const search = searchTerm.toLowerCase()

    return (
      [...timelineEvents]
        // Filter by decade
        .filter((event) => {
          if (!decade) return true
          const year = new Date(event.date).getFullYear()
          return year >= decade && year < decade + 10
        })
        // Filter by search term
        .filter((event) => {
          if (!searchTerm) return true
          return event.title.toLowerCase().includes(search) || event.description?.toLowerCase().includes(search)
        })
        // Filter by significance
        .filter((event) => {
          if (significanceFilter === "all") return true
          return event.significance === significanceFilter
        })
        // Sort by date (newest first)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    )
  }, [activeTab, searchTerm, significanceFilter])

  // Create ItemList schema with all timeline events
  const timelineItemListSchema = useMemo(
    () =>
      createItemListSchema(
        timelineEvents.map((event) => ({
          name: event.title,
          description: event.description,
          url: `https://rtefn.com/timeline#${event.date}`,
        })),
      ),
    [],
  )

  // Also create individual Event schemas for top events
  const eventSchemas = useMemo(
    () => filteredEvents.slice(0, 10).map((event) => createEventSchema(event)),
    [filteredEvents],
  )

  const getTimelineIcon = (significance?: string) => {
    switch (significance) {
      case "high":
        return <IconGavel size={20} />
      case "medium":
        return <IconNews size={20} />
      default:
        return <IconFileText size={20} />
    }
  }

  const getSignificanceColor = (significance?: string) => {
    switch (significance) {
      case "high":
        return "red"
      case "medium":
        return "orange"
      default:
        return "blue"
    }
  }

  return (
    <>
      <SEO
        title="Case Timeline"
        description="Interactive timeline of key events, arrests, court proceedings, and document releases in the Jeffrey Epstein case. Track the case progression from initial allegations to current status."
        canonical="https://rtefn.com/timeline"
        keywords="Epstein case timeline, chronology, key events, case history, important dates, Epstein timeline, court proceedings timeline, case progression, historical events"
        ogImage="https://rtefn.com/og-default.png"
        ogImageAlt="Epstein Case Timeline - Key Events and Court Proceedings"
      />
      <StructuredData data={[timelineItemListSchema, ...eventSchemas]} />
      <Box
        className={`min-h-screen ${
          colorScheme === "dark" ? "hero-dark" : "bg-gradient-to-br from-gray-50 via-white to-blue-50"
        }`}
        py="xl"
      >
        {colorScheme === "dark" && <Box className="noise-overlay" />}
        <Container size="lg" className="relative z-10">
          <Stack gap="xl">
            {/* Page Header */}
            <Stack ta="center" gap="md">
              <Group justify="center" gap="sm">
                <IconClock size={32} style={{ color: "var(--mantine-primary-color-filled)" }} />
                <Title order={1} size="h1">
                  Case Timeline
                </Title>
              </Group>
              <Text size="2xl" maw={768} mx="auto" c="dimmed" lh={1.6}>
                Comprehensive chronology of the Jeffrey Epstein case from early connections through recent document
                releases
              </Text>
            </Stack>

            {/* Filters */}
            <Paper p="lg" radius="md">
              <Stack gap="md">
                {/* Decade Tabs */}
                <Tabs value={activeTab} onChange={(value) => setActiveTab(value || "all")}>
                  <Tabs.List>
                    <Tabs.Tab value="all">All Years</Tabs.Tab>
                    <Tabs.Tab value="2020">2020s</Tabs.Tab>
                    <Tabs.Tab value="2010">2010s</Tabs.Tab>
                    <Tabs.Tab value="2000">2000s</Tabs.Tab>
                    <Tabs.Tab value="1990">1990s</Tabs.Tab>
                  </Tabs.List>
                </Tabs>

                {/* Search and Filter */}
                <Group grow>
                  <TextInput
                    placeholder="Search timeline events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.currentTarget.value)}
                    leftSection={<IconSearch size={16} />}
                  />
                  <Select
                    placeholder="Filter by significance"
                    value={significanceFilter}
                    onChange={(value) => setSignificanceFilter(value || "all")}
                    data={[
                      { value: "all", label: "All Significance" },
                      { value: "high", label: "High Impact" },
                      { value: "medium", label: "Medium Impact" },
                      { value: "low", label: "Background Events" },
                    ]}
                  />
                </Group>
              </Stack>
            </Paper>

            {/* Timeline */}
            <Paper p="xl" radius="lg">
              {filteredEvents.length === 0 ? (
                <Stack ta="center" py="xl" gap="md" align="center">
                  <IconSearch size={48} style={{ color: "var(--mantine-color-dimmed)" }} />
                  <Title order={3}>No events found</Title>
                  <Text c="dimmed">Try adjusting your search terms or filters to see more results.</Text>
                </Stack>
              ) : (
                <Timeline active={filteredEvents.length} bulletSize={32} lineWidth={2}>
                  {filteredEvents.map((event, index) => (
                    <Timeline.Item
                      key={index}
                      bullet={getTimelineIcon(event.significance)}
                      title={
                        <Stack gap="xs" mb="md">
                          <Group justify="space-between" align="flex-start" wrap="nowrap">
                            <Title order={3} className="flex-1" size="1.25rem">
                              {event.title}
                            </Title>
                            {event.significance && (
                              <Badge
                                color={getSignificanceColor(event.significance)}
                                variant="light"
                                size="sm"
                                className="ml-3 shrink-0"
                              >
                                {event.significance.toUpperCase()}
                              </Badge>
                            )}
                          </Group>
                          <Group gap="xs">
                            <IconCalendar size={16} />
                            <Text size="base" fw={500} c="blue">
                              {new Date(event.date).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </Text>
                          </Group>
                        </Stack>
                      }
                    >
                      <Box pb="lg">
                        {event.description && (
                          <Text size="base" c="dimmed" lh={1.6} maw={768} mb="md">
                            {event.description}
                          </Text>
                        )}

                        {event.document_ids && event.document_ids.length > 0 && (
                          <Paper p="md" radius="md" className="border-l-4 border-l-blue-400">
                            <Group gap="xs">
                              <IconFileText size={16} className="text-blue-500" />
                              <Text size="base" fw={500} c="blue">
                                {event.document_ids.length} related document{event.document_ids.length !== 1 ? "s" : ""}{" "}
                                available
                              </Text>
                            </Group>
                          </Paper>
                        )}
                      </Box>
                    </Timeline.Item>
                  ))}
                </Timeline>
              )}
            </Paper>

            {/* Summary Stats */}
            <Paper p="lg" radius="md">
              <Group justify="space-around">
                <Stack ta="center" gap="xs">
                  <Text size="xl" fw="bold">
                    {filteredEvents.filter((e) => e.significance === "high").length}
                  </Text>
                  <Text size="sm" c="red">
                    High Impact Events
                  </Text>
                </Stack>
                <Stack ta="center" gap="xs">
                  <Text size="xl" fw="bold">
                    {filteredEvents.filter((e) => e.significance === "medium").length}
                  </Text>
                  <Text size="sm" c="orange">
                    Medium Impact Events
                  </Text>
                </Stack>
                <Stack ta="center" gap="xs">
                  <Text size="xl" fw="bold">
                    {filteredEvents.length}
                  </Text>
                  <Text size="sm" c="blue">
                    Total Events
                  </Text>
                </Stack>
                <Stack ta="center" gap="xs">
                  <Text size="xl" fw="bold">
                    {new Date().getFullYear() - 1991}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Years Covered
                  </Text>
                </Stack>
              </Group>
            </Paper>

            {/* Call to Action */}
            <PageCTASection contextualAction={{ href: "/documents", label: "View Documents", icon: IconFileText }} />
          </Stack>
        </Container>
      </Box>
    </>
  )
}
